import { ArchivedWebDashboardRecord } from "@coeff/api";
import { message, Modal, Radio, Table, Tag } from "antd";
import React, { useCallback, useState } from "react";
import styled from "styled-components";

import { DEPRECATED_Button, Input } from "../../components";
import { COLORS } from "../../constants";
import { useApiContext } from "../../utils";

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 800;
  margin: 0 0 4px 0;
`;

const Subtitle = styled.p`
  color: ${COLORS.black45};
  margin: 0 0 20px 0;
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 20px;
`;

const Dash = () => <span style={{ color: COLORS.black45 }}>—</span>;

type SearchType = "email" | "domain";

export const ArchivedDashboards: React.FC = () => {
  const { apiClient } = useApiContext();

  const [searchType, setSearchType] = useState<SearchType>("domain");
  const [searchValue, setSearchValue] = useState("");
  const [loadedLabel, setLoadedLabel] = useState("");
  const [dashboards, setDashboards] = useState<ArchivedWebDashboardRecord[]>([]);
  const [restoreLimit, setRestoreLimit] = useState(5);
  const [loading, setLoading] = useState(false);

  // Restore confirmation modal state.
  const [restoreTarget, setRestoreTarget] = useState<ArchivedWebDashboardRecord | null>(null);
  const [note, setNote] = useState("");
  const [restoring, setRestoring] = useState(false);

  const search = useCallback(async () => {
    const trimmed = searchValue.trim().toLowerCase();
    if (!trimmed) {
      return;
    }
    setLoading(true);
    try {
      const res = await apiClient.getArchivedWebDashboards(
        searchType === "email" ? { email: trimmed } : { domain: trimmed }
      );
      setDashboards(res.data.dashboards);
      setRestoreLimit(res.data.restore_limit);
      setLoadedLabel(trimmed);
    } catch {
      message.error("Failed to load archived dashboards");
    } finally {
      setLoading(false);
    }
  }, [apiClient, searchType, searchValue]);

  const handleRestore = async () => {
    if (!restoreTarget || note.trim() === "") {
      return;
    }
    setRestoring(true);
    try {
      await apiClient.forceRestoreWebDashboard({
        web_dashboard_id: restoreTarget.web_dashboard_id,
        force_restore_web_dashboard_request: { note: note.trim() },
      });
      message.success("Dashboard restored");
      // Drop the restored row locally so the list reflects the change without a full refetch.
      setDashboards(prev =>
        prev.filter(d => d.web_dashboard_id !== restoreTarget.web_dashboard_id)
      );
      setRestoreTarget(null);
      setNote("");
    } catch {
      message.error("Failed to restore dashboard");
    } finally {
      setRestoring(false);
    }
  };

  const columns = [
    {
      title: "Dashboard",
      dataIndex: "dashboard_name",
      key: "dashboard_name",
      render: (name: string | undefined) => name || <Dash />,
    },
    {
      title: "Owner",
      dataIndex: "owner_email",
      key: "owner_email",
    },
    {
      title: "Domain",
      dataIndex: "owner_domain",
      key: "owner_domain",
      width: 200,
    },
    {
      title: "Archived",
      dataIndex: "archived_dt",
      key: "archived_dt",
      width: 180,
      render: (dt: string) => (
        <span style={{ whiteSpace: "nowrap" }}>{new Date(dt).toLocaleString()}</span>
      ),
    },
    {
      title: "Restores used",
      key: "restore_count",
      width: 130,
      render: (_: unknown, record: ArchivedWebDashboardRecord) => (
        <span>
          {record.restore_count} / {restoreLimit}
        </span>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 160,
      render: (_: unknown, record: ArchivedWebDashboardRecord) =>
        record.is_restore_locked ? (
          <Tag color="red">Restore limit reached</Tag>
        ) : (
          <Tag color="orange">Archived</Tag>
        ),
    },
    {
      title: "",
      key: "actions",
      width: 120,
      render: (_: unknown, record: ArchivedWebDashboardRecord) => (
        <DEPRECATED_Button
          size="small"
          type={record.is_restore_locked ? "primary" : "default"}
          onClick={() => {
            setRestoreTarget(record);
            setNote("");
          }}
        >
          Restore
        </DEPRECATED_Button>
      ),
    },
  ];

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", padding: 24 }}>
      <PageTitle>Archived Dashboards</PageTitle>
      <Subtitle>
        Find a user's or domain's archived dashboards and restore ones that have hit the
        self-service restore limit.
      </Subtitle>

      <SearchBar>
        <Radio.Group
          value={searchType}
          onChange={e => setSearchType(e.target.value as SearchType)}
          optionType="button"
          buttonStyle="solid"
        >
          <Radio.Button value="domain">Domain</Radio.Button>
          <Radio.Button value="email">User Email</Radio.Button>
        </Radio.Group>
        <Input
          style={{ width: 320 }}
          placeholder={searchType === "email" ? "user@company.com" : "company.com"}
          value={searchValue}
          onChange={e => setSearchValue(e.target.value)}
          onPressEnter={search}
          allowClear
        />
        <DEPRECATED_Button type="primary" onClick={search} loading={loading}>
          Search
        </DEPRECATED_Button>
        {loadedLabel && (
          <span style={{ fontSize: 13, color: COLORS.black45 }}>
            {dashboards.length} archived dashboard(s) for <strong>{loadedLabel}</strong>
          </span>
        )}
      </SearchBar>

      <div style={{ flex: 1, minHeight: 0 }}>
        <Table
          columns={columns}
          dataSource={dashboards}
          rowKey="web_dashboard_id"
          loading={loading}
          pagination={{ showTotal: total => `${total} dashboards`, pageSize: 50 }}
          size="small"
          scroll={{ x: "auto" }}
          locale={{
            emptyText: loadedLabel
              ? "No archived dashboards found"
              : "Search by domain or user email to see archived dashboards",
          }}
        />
      </div>

      <Modal
        title="Restore dashboard"
        open={restoreTarget !== null}
        onCancel={() => {
          setRestoreTarget(null);
          setNote("");
        }}
        okText="Restore"
        okButtonProps={{ disabled: note.trim() === "", loading: restoring }}
        onOk={handleRestore}
      >
        {restoreTarget && (
          <>
            <p>
              Restore{" "}
              <strong>{restoreTarget.dashboard_name || restoreTarget.web_dashboard_id}</strong>{" "}
              owned by <strong>{restoreTarget.owner_email}</strong>?
            </p>
            <p style={{ color: COLORS.black45, fontSize: 13 }}>
              This unarchives the dashboard. The restore count ({restoreTarget.restore_count} /{" "}
              {restoreLimit}) is left unchanged, so the owner will still hit the limit if they
              archive and restore again.
            </p>
            <Input.TextArea
              placeholder="Reason for this restore (required)"
              value={note}
              onChange={e => setNote(e.target.value)}
              rows={3}
            />
          </>
        )}
      </Modal>
    </div>
  );
};
